import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik, FormikErrors, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { Clear } from "@material-ui/icons";
import Chip from "@material-ui/core/Chip";
import CustomDialog, { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomButton from "../../../components/Button/CustomButton";
import { tesaBlue, tesaBlueProfessional, tesaDarkGrey, tesaGrey1, tesaGrey6 } from "../../../styles/colors";
import TesaTmbApi from "../../../api/services/TesaTmbApi";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import { msgSubmissionInvalid } from "../messages";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
// prettier-ignore
import {
  TestSpecificationSearchMultipleAutocomplete,
} from "../../../components/Autocomplete/CustomSearchAutoComplete.instances";
import TestSpecificationMessage from "./TestSpecificationMessage";
import { TestSpecification } from "./testTab.models";
import CustomProgress from "../../../components/Alert/CustomProgress";
import scrollToError from "../../../functions/scrollToError.function";
import TestPlanResponse from "../../../api/models/test-series/TestPlanResponse";
import TestSpecificationLifecycleReference from "../../../api/models/tmb/TestSpecificationLifecycleReference";
import { TestSeriesContext } from "../../../contexts/TestSeriesContext";

interface TestSpecificationForm {
  testSpecifications: TestSpecification[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    alignRight: {
      textAlign: "right",
    },
    bold: {
      fontWeight: "bold",
    },
    chip: {
      color: tesaDarkGrey,
      backgroundColor: tesaGrey6,

      "& .MuiChip-deleteIcon": {
        color: tesaGrey1,
      },
    },
    header: {
      marginBottom: theme.spacing(5),
    },
    marginTop: {
      marginTop: theme.spacing(2),
    },
    link: {
      color: tesaBlue,
      cursor: "pointer",
      fontSize: "0.875rem",
      lineHeight: "1rem",
      letterSpacing: 1.25,
      paddingLeft: 0,
      paddingRight: 0,
      fontFamily: "Proxima Nova",
      "&:hover": {
        color: tesaBlueProfessional,
      },
    },
    loading: {
      "& .MuiCircularProgress-root": {
        position: "fixed",
        margin: "auto",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
      },
    },
  }),
);

interface CustomAutocompleteTagProps {
  key: string;
  label: string;
}

const CustomAutocompleteTag: React.FC<CustomAutocompleteTagProps & { children?: null }> = (props) => {
  const { chip } = useStyles();
  return <Chip classes={{ root: chip }} deleteIcon={<Clear />} {...props} />;
};

const getDeprecatedSpecifications = (
  specifications: TestSpecification[],
  specificationLifecycleReferences: TestSpecificationLifecycleReference[],
) =>
  specifications.filter((s) => {
    const lifecycleById = specificationLifecycleReferences.find(
      (slr) => slr.id === s.testPlanVersionId!,
    )?.lifeCycleStateByVersion;
    return lifecycleById?.[s.testPlanVersionVersion!] === "DEPRECATED";
  });

const getPreselectedSpecifications = (
  specifications: TestSpecification[],
  deprecatedSpecifications: TestSpecification[],
) => specifications.filter((s) => !deprecatedSpecifications.some((ds) => ds.testPlanVersionId === s.testPlanVersionId));

const getUnselectedSpecifications = (
  testSpecs: TestSpecification[],
  testPlans: TestPlanResponse[],
): TestSpecification[] =>
  testSpecs.filter(
    (ts) =>
      !testPlans.some(
        (tp) => tp.tmbTestPlanId === ts.testPlanVersionId && tp.tmbTestPlanVersion === ts.testPlanVersionVersion,
      ),
  );

interface TestSpecificationDialogProps extends CustomDialogProps {
  onSelected: (testSpecifications: TestSpecification[]) => void;
  specifications: TestSpecification[];
}

const TestSpecificationDialog: React.FC<TestSpecificationDialogProps> = (props) => {
  const { open, onClose, onSelected, specifications } = props;
  const classes = useStyles();
  const dialogContext = useContext(ConfirmationDialogContext);
  const formRef = useRef<FormikProps<TestSpecificationForm>>(null);
  const { showErrorSnackbar } = useContext(ErrorSnackbarContext);
  const { testPlans } = useContext(TestSeriesContext);
  const [loading, setLoading] = useState(false);
  const [deprecatedSpecifications, setDeprecatedSpecifications] = useState<TestSpecification[]>([]);
  const [preselectedSpecifications, setPreselectedSpecifications] = useState<TestSpecification[]>([]);

  const tesaTmbApi = new TesaTmbApi(dialogContext);
  const yupValidationSchema = Yup.object().shape({
    testSpecifications: Yup.array().nullable().required("Please select"),
  });

  useEffect(() => {
    if (open && specifications.length > 0) {
      setLoading(true);
      let doUpdate = true;
      tesaTmbApi.searchTestSpecificationStatus(specifications.map((s) => s.testPlanVersionId!)).then((res) => {
        if (doUpdate) {
          const unselectedSpecs = getUnselectedSpecifications(specifications, testPlans);
          const deprecatedSpecs = getDeprecatedSpecifications(unselectedSpecs, res.data);
          const preselectedSpecs = getPreselectedSpecifications(unselectedSpecs, deprecatedSpecs);
          setDeprecatedSpecifications(deprecatedSpecs);
          setPreselectedSpecifications(preselectedSpecs);
          formRef.current?.setFieldValue("testSpecifications", preselectedSpecs);
          setLoading(false);
        }
      });

      return () => {
        doUpdate = false;
      };
    }
  }, [open, specifications]);

  if (loading) {
    return (
      <div className={classes.loading}>
        <CustomProgress />
      </div>
    );
  }

  return (
    <CustomDialog fullWidth maxWidth="sm" onClose={() => onClose()} open={open}>
      <h2>ADD TEST SPECIFICATION</h2>
      <TestSpecificationMessage specifications={preselectedSpecifications} />
      <TestSpecificationMessage specifications={deprecatedSpecifications} warn />
      <Formik<TestSpecificationForm>
        innerRef={formRef}
        initialValues={{
          testSpecifications: preselectedSpecifications,
        }}
        onSubmit={(values, actions) => {
          const { testSpecifications: newTestSpecifications } = values;
          if (newTestSpecifications) {
            actions.resetForm();
            onSelected(newTestSpecifications);
          } else {
            onClose();
          }
        }}
        validationSchema={yupValidationSchema}
      >
        {(formikProps: FormikProps<TestSpecificationForm>): JSX.Element => {
          const { errors, isSubmitting, touched, values, setFieldValue } = formikProps;
          return (
            <Form>
              <Grid container justify="flex-start" direction="row" spacing={4}>
                <Grid item xs={12}>
                  <TestSpecificationSearchMultipleAutocomplete
                    autoFocus
                    customTag={CustomAutocompleteTag}
                    error={!!(errors.testSpecifications && touched.testSpecifications)}
                    helperText={[
                      errors.testSpecifications && touched.testSpecifications ? errors.testSpecifications : "",
                    ].join(" ")}
                    inputLabel="Specifications"
                    map={(value): { key: string; label: string } => ({
                      key: `${value.testPlanVersionId || value.testPlanVersionName}`,
                      label: `${value.testPlanVersionName}`,
                    })}
                    onChange={(event, newSpecifications): void => {
                      const lastItem = newSpecifications[newSpecifications.length - 1];
                      if (
                        newSpecifications.length <= (values.testSpecifications || []).length ||
                        !(values.testSpecifications || []).some(
                          (spec) => spec.testPlanVersionName === lastItem.testPlanVersionName,
                        )
                      ) {
                        setFieldValue("testSpecifications", newSpecifications);
                      }
                    }}
                    optionFetch={(query) =>
                      tesaTmbApi.searchTestSpecificationVersions(query, 50).then((pagedSpecifications) => ({
                        ...pagedSpecifications,
                        data: pagedSpecifications.data.map((specification) => ({
                          testPlanVersionId: specification.id,
                          testPlanVersionName: specification.name,
                          testPlanVersionVersion: specification.version,
                        })),
                      }))
                    }
                    placeholder="Add further specifications"
                    value={values.testSpecifications}
                  />
                </Grid>
              </Grid>
              <Grid container item direction="row" justify="flex-end">
                <CustomButton
                  className={classes.marginTop}
                  disabled={isSubmitting || values.testSpecifications.length === 0}
                  onClick={() => {
                    if (formRef.current) {
                      const form = formRef.current;
                      return form.validateForm().then((formErrors: FormikErrors<TestSpecificationForm>) => {
                        if (Object.keys(formErrors).length > 0) {
                          form.handleSubmit();
                          scrollToError();
                          showErrorSnackbar(msgSubmissionInvalid);
                        }
                      });
                    }
                    return Promise.reject();
                  }}
                  primary
                  text="Use"
                  type="submit"
                />
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </CustomDialog>
  );
};

export default TestSpecificationDialog;
